/* eslint-disable react/jsx-props-no-spreading,react/prop-types */
import * as React from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';

import {
  InputTextWithLabel,
  InputBaseContainer,
  ButtonWithLoader,
} from '../molecules';
import { FormServerError } from '../types';
import isYupFieldRequired from '../utils/isYupFieldRequired';
import transformServerErrorsToForm from '../utils/transformServerErrorsToForm';
import { getInspectorMotdAudience } from '../api/inspectorMotd';

const labels = {
  text: 'Message',
};

const schema = yup.object().shape({
  text: yup.string().required().label(labels.text),
});

/**
 * Compose or edit an Inspector Message of the Day.
 *
 * There is deliberately no Addresses picker. The audience belongs to the Super
 * Admin, in Settings > Message Setup — either every inspector, or the list they
 * chose — so the sender has nothing to decide. We show who it reaches instead.
 */
const InspectorMotdForm = ({
  onSubmit,
  modalError,
  initialValues,
  initialErrors,
  addButtonText = 'Send',
  updateButtonText = 'Save',
}: Props) => {
  const isEditing = Boolean(initialValues?.id);

  const {
    register,
    handleSubmit,
    errors,
    setError,
    formState,
  } = useForm<InspectorMotdFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      id: initialValues?.id,
      text: initialValues?.text || '',
    } as InspectorMotdFormValues,
  });

  const [isSubmitting, setIsSubmitting] = React.useState<
    boolean
  >(false);

  React.useEffect(() => {
    if (
      formState.isSubmitting &&
      !Object.keys(errors).length
    ) {
      setIsSubmitting(formState.isSubmitting);
    }
  }, [formState]);

  const [recipients, setRecipients] = React.useState<
    string | null
  >(null);

  React.useEffect(() => {
    // The audience is fixed at send time and can't be rewritten afterwards, so
    // there's nothing worth showing while editing.
    if (isEditing) return;

    getInspectorMotdAudience()
      .then(response => {
        const { sendToAll, recipients: list } =
          response.data || ({} as any);

        if (sendToAll) {
          setRecipients('All inspectors');
          return;
        }

        setRecipients(
          (list || []).map(item => item.name).join(', ') ||
            'All inspectors',
        );
      })
      .catch(() => {});
  }, [isEditing]);

  React.useEffect(() => {
    if (initialErrors) {
      transformServerErrorsToForm<InspectorMotdFormValues>(
        initialErrors,
      ).forEach(({ name, types }) =>
        setError(name, { types }));
    }
  }, [initialErrors]);

  React.useEffect(() => {
    if (Object.keys(errors).length) {
      setIsSubmitting(false);
    }
  }, [errors, initialErrors]);

  const middleware = (
    submitFn: (data: any) => void,
  ) => (data: InspectorMotdFormValues) => {
    if (isEditing) {
      submitFn({ id: initialValues.id, text: data.text });
      return;
    }

    // No addresses: the backend reads that as the whole configured audience.
    submitFn({ text: data.text });
  };

  return (
    <Container disabled={isSubmitting && !modalError}>
      <FormContainer
        autoComplete="off"
        aria-autocomplete="none"
        onSubmit={handleSubmit(middleware(onSubmit))}
      >
        {!isEditing && recipients && (
          <Recipients>
            <RecipientsLabel>Sends to</RecipientsLabel>
            <RecipientsValue>{recipients}</RecipientsValue>
          </Recipients>
        )}

        <InputBaseContainer>
          <InputTextWithLabel
            name="text"
            inputRef={register}
            error={errors.text}
            label={labels.text}
            rows={2}
            rowsMax={4}
            multiline
            isRequired={isYupFieldRequired('text', schema)}
          />
        </InputBaseContainer>

        <ButtonWithLoader
          disabled={isSubmitting && !modalError}
          loading={isSubmitting && !modalError}
          type="submit"
        >
          {isEditing ? updateButtonText : addButtonText}
        </ButtonWithLoader>
      </FormContainer>
    </Container>
  );
};

const Container = styled.fieldset`
  display: block;
  margin: 0;
  padding: 0;
  width: 100%;
  border: none;
  text-align: center;
`;

const FormContainer = styled.form`
  width: 100%;

  .MuiButton-root {
    margin-top: 24px;
  }
`;

const Recipients = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 14px;
  margin-bottom: 16px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-50);
  box-sizing: border-box;
  text-align: left;
`;

const RecipientsLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
  flex-shrink: 0;
`;

const RecipientsValue = styled.span`
  font-size: 0.875rem;
  color: var(--mainText);
  min-width: 0;
`;

export interface InspectorMotdFormValues {
  id?: number;
  text: string;
}

interface Props {
  onSubmit: (data: any) => void;
  modalError: boolean;
  initialValues?: { id?: number; text?: string };
  initialErrors?: FormServerError<InspectorMotdFormValues>;
  addButtonText?: string;
  updateButtonText?: string;
}

export default InspectorMotdForm;
